import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, takeLatest, all } from "redux-saga/effects";
import {
  fetchQmsElements,
  fetchQmsElementsSuccess,
  fetchQmsElementsFailure,
  fetchQmsElementById,
  fetchQmsElementByIdSuccess,
  fetchQmsElementByIdFailure,
  createQmsElement,
  createQmsElementSuccess,
  createQmsElementFailure,
  updateQmsElement,
  updateQmsElementSuccess,
  updateQmsElementFailure,
  deleteQmsElement,
  deleteQmsElementSuccess,
  deleteQmsElementFailure,
  QmsElement,
  FetchQmsElement,
} from "../reducers/qmsSlice";
import axiosInstance from "services/axios";
import { API_ENDPOINTS } from "constants/api";
import { notify } from "utils/notification";

function* fetchQmsElementsSaga(): Generator<any, void, any> {
  try {
    const response = yield call(
      axiosInstance.get,
      API_ENDPOINTS.ELEMENT.ELEMENTS,
    );
    yield put(fetchQmsElementsSuccess(response.data));
  } catch (error: any) {
    yield put(fetchQmsElementsFailure(error.message));
  }
}

function* fetchQmsElementByIdSaga(
  action: ReturnType<typeof fetchQmsElementById>,
): Generator<any, void, { data: FetchQmsElement }> {
  try {
    // const response = yield call(axiosInstance.get, `${API_ENDPOINTS.ELEMENT.ELEMENTS}/${action.payload}`);
    const response = yield call(
      axiosInstance.get,
      `${API_ENDPOINTS.ELEMENT.ELEMENTS}/${action.payload}`,
    );

    yield put(fetchQmsElementByIdSuccess(response.data.data));
  } catch (error: any) {
    yield put(fetchQmsElementByIdFailure(error.message));
  }
}

function* createQmsElementSaga(
  action: PayloadAction<Omit<QmsElement, "key">>,
): Generator<any, void, any> {
  try {
    const response = yield call(
      axiosInstance.post,
      API_ENDPOINTS.ELEMENT.ELEMENTS,
      { ...action.payload },
    );
    notify.success({
      message: "Success",
      description: response.data.message || "QMS Element created successfully",
    });
    yield put(createQmsElementSuccess(response.data));
  } catch (error: any) {
    yield put(createQmsElementFailure(error.message));
  }
}

function* updateQmsElementSaga(
  action: PayloadAction<QmsElement>,
): Generator<any, void, any> {
  try {
    const response = yield call(
      axiosInstance.post,
      `${API_ENDPOINTS.ELEMENT.ELEMENTS}`,
      action.payload,
    );

    yield put(updateQmsElementSuccess(response.data));

    if (action.payload.id) {
      yield put(fetchQmsElementById(action.payload.id));
    }

    notify.success({
      message: "Success",
      description: response.data.message,
    });
  } catch (error: any) {
    yield put(updateQmsElementFailure(error.message));
  }
}

function* deleteQmsElementSaga(
  action: PayloadAction<string>,
): Generator<any, void, any> {
  try {
    const response = yield call(
      axiosInstance.delete,
      `${API_ENDPOINTS.ELEMENT.ELEMENTS}/${action.payload}`,
    );

    if (response.status === 200) {
      yield put(deleteQmsElementSuccess(action.payload));
      yield put(fetchQmsElements()); // Refresh the list after successful deletion
      notify.success({
        message: "Success",
        description:
          response.data.message || "QMS Element deleted successfully",
      });
    }
  } catch (error: any) {
    yield put(deleteQmsElementFailure(error.message));
    notify.error({
      message: "Error",
      description:
        error.response?.data?.message || "Failed to delete QMS Element",
    });
  }
}

export function* qmsSaga() {
  yield all([
    takeLatest(fetchQmsElements.type, fetchQmsElementsSaga),
    takeLatest(fetchQmsElementById.type, fetchQmsElementByIdSaga),
    takeLatest(createQmsElement.type, createQmsElementSaga),
    takeLatest(updateQmsElement.type, updateQmsElementSaga),
    takeLatest(deleteQmsElement.type, deleteQmsElementSaga),
  ]);
}
